import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { SegmentedButton, Icon } from '@ontruck/orc';
import DocumentsUpload from 'components/DocumentsUpload';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  getDriverDocumentsByUuid,
  getVehicleDocumentsByUuid,
  getCarrierDocumentsByUuid,
  fetcher,
} from 'utils/network/http';
import Spinner from 'components/Spinner';
import { STATUS } from 'utils/constants/documents';
import useSWR from 'swr';
import config from 'utils/constants/config';
import { sendEventToSegment } from 'utils/functions/Tracking';
import TRACKING_EVENTS from 'utils/constants/trackingEvents';
import useDocsCarrierProfile from 'hooks/useDocsCarrierProfile/useDocsCarrierProfile';
import qs from 'query-string';
import DocumentsAccordion from 'components/DocumentsAccordion';
import DocumentsFunnel from 'modules/Documentation/DocumentsFunnel';

const FILTERS = {
  ALL: 'ALL',
  PENDING: 'PENDING',
};

const PENDING_STATUSES = [STATUS.status_not_uploaded];
const VALID_STATUSES = [STATUS.status_validated, STATUS.status_close_to_expire];

const DocumentsList = ({ className }) => {
  const { t } = useTranslation('Documents');
  const [filter, setFilter] = useState(FILTERS.ALL);
  const {
    loadingProfile,
    carrierProfile,
    error: profileError,
    carrierUuid,
  } = useDocsCarrierProfile();
  const location = useLocation();
  const parsedURL = qs.parse(location.search);
  const isStaff = parsedURL?.staff;
  const missing_documents = parsedURL?.missing_documents?.split(',');

  const handleFilters = useCallback((e, { value }) => {
    setFilter(value);
    sendEventToSegment(TRACKING_EVENTS.DMS_FILTER_CLICKED, {
      filterValue: value,
    });
  }, []);

  const filterForPending = (document) =>
    filter === FILTERS.ALL ||
    (filter === FILTERS.PENDING && PENDING_STATUSES.includes(document.status));

  const { data: documentsResponse, error, mutate: refetchDocuments } = useSWR(
    carrierUuid && (carrierProfile?.terms_and_conditions_accepted || isStaff)
      ? getCarrierDocumentsByUuid(carrierUuid)
      : null,
    fetcher,
    {
      refreshInterval: config.SLOW_POLLING_DELAY,
    }
  );

  if (error || profileError) {
    return (
      <div className={className} data-testid="documents-list">
        <div className="documents-list">
          <div
            className="documents-list__error"
            data-testid="documents-list__error"
          >
            {t('errorGettingDocuments')}
          </div>
        </div>
      </div>
    );
  }

  if (!documentsResponse || loadingProfile) {
    return (
      <div className={className} data-testid="documents-list">
        <div className="documents-list">
          <div
            className="documents-list__loading"
            data-testid="documents-list__loading"
          >
            <Spinner />
          </div>
        </div>
      </div>
    );
  }

  const {
    documents,
    name,
    not_uploaded_count,
    drivers,
    vehicles,
  } = documentsResponse?.data;

  const hasDocumentsAndFilters =
    documents.length > 0 &&
    (filter === FILTERS.ALL ||
      (filter === FILTERS.PENDING && not_uploaded_count > 0));

  const invalidDocuments = documents
    .filter((doc) => !VALID_STATUSES.includes(doc.status))
    .map((doc) => doc.document_type.id);

  const filterNonValid = (document) =>
    invalidDocuments.includes(parseInt(document));

  return (
    <div className={className} data-testid="documents-list">
      <div className="documents-list">
        <div className="documents-list__container">
          <DocumentsFunnel activeStep={2} />
          <h1 className="documents-list__title">{t('documents')}</h1>
          {not_uploaded_count > 0 && (
            <div className="documents-list__top-container">
              <div className="documents-list__filters">
                <SegmentedButton.Parent
                  size="M"
                  value={filter}
                  onChange={handleFilters}
                  dataE2E="documents-list__filters"
                >
                  <SegmentedButton.Child
                    value={FILTERS.PENDING}
                    //label={`${t('filtersPending')} (${not_uploaded_count})`} We are going to update this in the next iteration
                    label={`${t('filtersPending')}`}
                    dataE2E="documents-list__filters--pending"
                  />
                  <SegmentedButton.Child
                    value={FILTERS.ALL}
                    label={t('filtersAll')}
                    dataE2E="documents-list__filters--all"
                  />
                </SegmentedButton.Parent>
              </div>
              <div className="documents-list__format-warning">
                <span className="documents-list__format-warning--icon">
                  <Icon glyph="girded-info" size="1.6rem" />
                </span>
                <span className="documents-list__format-warning--text">
                  {t('validFormats')}
                </span>
              </div>
            </div>
          )}
          {missing_documents &&
            missing_documents.filter(filterNonValid).length > 0 && (
              <div
                className="documents-list__missing-documents"
                data-testid="missing-documents"
              >
                <span className="documents-list__missing-documents--text">
                  <div>{t(`missingDocsWarning`)}</div>
                  <br />
                  {missing_documents.filter(filterNonValid).map((document) => {
                    return <div>- {t(`documentType${document}`)}</div>;
                  })}
                </span>
              </div>
            )}
          {hasDocumentsAndFilters && (
            <div className="documents-list__sublist">
              <h3 className="documents-list__list-title">{name}</h3>
              <div className="documents-list__sublist-columns">
                <span className="documents-list__column documents-list__column--expire">
                  {t('expire')}
                </span>
                <span className="documents-list__column documents-list__column--state">
                  {t('state')}
                </span>
              </div>
              {documents?.filter(filterForPending).map((document, index) => {
                return (
                  <DocumentsUpload
                    document={document}
                    entity_type="carrier"
                    uuid={carrierUuid}
                    refetchDocuments={refetchDocuments}
                    key={`${name}-${index}`}
                  />
                );
              })}
            </div>
          )}
          {vehicles?.length > 0 && (
            <div className="documents-list__entity">
              <div className="documents-list__entity-title">
                <span>
                  <h2 className="documents-list__list-title documents-list__list-title--sublist">
                    {t('vehicles')}
                  </h2>
                </span>
                <span className="documents-list__list-subtitle">
                  ({t('noRequiredDocs')})
                </span>
              </div>
              {vehicles.map((vehicle) => {
                return (
                  <DocumentsAccordion
                    item={vehicle}
                    request={getVehicleDocumentsByUuid}
                    areTermsAccepted={
                      carrierProfile?.terms_and_conditions_accepted
                    }
                    isStaff={isStaff}
                    filterForPending={filterForPending}
                    key={vehicle.entity_uuid}
                  />
                );
              })}
            </div>
          )}
          {drivers?.length > 0 && (
            <div className="documents-list__entity">
              <div className="documents-list__entity-title">
                <span>
                  <h2 className="documents-list__list-title documents-list__list-title--sublist">
                    {t('drivers')}
                  </h2>
                </span>
                <span className="documents-list__list-subtitle">
                  ({t('noRequiredDocs')})
                </span>
              </div>
              {drivers.map((driver) => {
                return (
                  <DocumentsAccordion
                    item={driver}
                    request={getDriverDocumentsByUuid}
                    carrierUuid={carrierUuid}
                    areTermsAccepted={
                      carrierProfile?.terms_and_conditions_accepted
                    }
                    isStaff={isStaff}
                    filterForPending={filterForPending}
                    key={driver.entity_uuid}
                  />
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
DocumentsList.propTypes = {
  className: PropTypes.string,
};
export default DocumentsList;
